import type { WebSocketResponse } from "@/types/types";
import { BaseHandler } from "./base.handler";
import { getLanguageCookie, getCurrencyCookie } from "@/utils";
import { WebSocketService } from "../websocket.service";
import {
  Configure,
  isConfigureResponse,
  RESULT,
  type ConfigureResponse,
} from "@/types";
import { useAppStore } from "@/stores";

export class ConfigHandler extends BaseHandler {
  async handle(message: WebSocketResponse): Promise<void> {
    if (!this.validateMessage(message)) {
      return;
    }

    if (isConfigureResponse(message)) {
      this.handleConfigureResponse(message);
    }
  }

  canHandle(messageType: string): boolean {
    return messageType === Configure;
  }

  sendConfiguration(): void {
    const payload: Record<string, string> = {
      language: getLanguageCookie(),
    };

    const currency = getCurrencyCookie();
    if (currency) {
      payload.currency = currency;
    }

    WebSocketService.getInstance().send(Configure, payload);
  }

  private handleConfigureResponse(message: ConfigureResponse): void {
    if (message.result !== RESULT.OK) return;

    const store = useAppStore.getState();
    const response = message as ConfigureResponse & {
      theme?: Record<string, string>;
      context?: { language?: string };
      overlay_id?: string | null;
      docked_overlay_since?: string | null;
    };

    // Prefer the language the backend resolved over the one we asked for: it falls back to a base
    // language when the casino doesn't offer the exact variant (`pt-br` -> `pt`), and the UI has to
    // render in whatever language the content will actually arrive in.
    const language = response.context?.language ?? getLanguageCookie();
    const currency = getCurrencyCookie() ?? null;

    if (response.theme) {
      store.setTheme(response.theme);
    }

    // `Casino.is_game_provider`, inferred from the theme's SHAPE: a provider
    // casino resolves to the IGW theme schema, whose `igw_button_anchor` has a
    // default and is therefore always present; the OSW schema forbids extra
    // keys, so a non-provider theme can never carry it (widgets/schema/theme.py).
    // Same implicit signal that places the in-game button. If that coupling
    // ever breaks, promote this to an explicit `is_provider` field on the
    // configure response instead of patching the inference.
    store.setIsProvider(
      response.theme != null && "igw_button_anchor" in response.theme
    );

    store.setOverlayId(response.overlay_id ?? null);
    store.setDockedOverlaySince(response.docked_overlay_since ?? null);
    store.setConfig(language, currency);
  }
}

export const configHandler = new ConfigHandler();
